import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import * as R from 'ramda';
import { withTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableSortLabel from '@mui/material/TableSortLabel';
import TableRow from '@mui/material/TableRow';

import { editProjectParticipants } from '../../store/actions/projectActions';
import { returnField } from '../../utils/formHelpers';
import { ascComparator, descComparator } from '../../utils/comparators';
import { Field } from '../Field/Field.js'
import { fastDeepEqual } from '../../utils/object.js'


const inlineStyles = {
  rightIcon: {
    marginLeft: 1,
  },
  tableLabel: {
    cursor: 'pointer',
    backgroundColor: 'rgb(227, 238, 255)',
    borderBottom: '2px solid black',
    height: '60px',
  },
};

const isActiveAndCandidate = val => R.path(['active'], val) && R.path(['candidate'], val);

const AddParticipants = ({
  doc,
  editProjectParticipants,
  filteredAttachedSpaces,
  initialValues,
  projectStatus,
  projectUsers,
  t,
  users
}) => {
  const [formValues, setFormValues] = useState(initialValues)
  const [sortOrder, setSortOrder] = useState('asc');
  const [orderByStr, setOrderByStr] = useState('company');
  const [submitting, setSubmitting] = useState(false)

  const pristine = fastDeepEqual(formValues, initialValues)
  const allSelected = Object.values(formValues).every(value => value)

  const isDraft = projectStatus === 'Draft'

  const handleSelectAll = useCallback(() => {
    setFormValues(
      Object.keys(formValues).reduce((acc, currentKey) => {
        acc[currentKey] = !allSelected
        return acc
      }, {})
    )
  }, [
    allSelected,
    formValues
  ])

  const resetForm = useCallback(() => {
    setFormValues(initialValues)
  }, [initialValues]);

  const userArr = R.values(users);

  // To sort data, we need to enhance the user array to contain some extra variables to match the table head labels
  const enhancedUserArr = userArr.map(user => {
    const uid = R.path(['profile', 'uid'], user);

    const isOwnedByThisUser = val => R.path(['users', uid], val) !== undefined;
    const filteredRelatedToThisUserAttachedSpaces = R.pickBy(
      isOwnedByThisUser,
      filteredAttachedSpaces
    );

    const filteredRelatedToThisUserActiveAndCandidateAttachedSpaces = R.pickBy(
      isActiveAndCandidate,
      filteredRelatedToThisUserAttachedSpaces
    );

    return {
      ...user,
      participationStatus: projectUsers[uid],
      spacesAdded: `${R.prop(
        'length',
        R.keys(filteredRelatedToThisUserActiveAndCandidateAttachedSpaces)
      )}/${R.prop('length', R.keys(filteredRelatedToThisUserAttachedSpaces))}`,
    };
  });

  // Filter out disabled users from participants list
  const filteredUserArr = enhancedUserArr.filter(
    user => !R.path(['mgmt', 'locked'], user)
  );

  const changeSortOrder = newOrderStr => {
    if (newOrderStr === orderByStr) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderByStr(newOrderStr);
    }
  };

  const rows = R.sort(
    sortOrder === 'asc'
      ? ascComparator(orderByStr)
      : descComparator(orderByStr),
    filteredUserArr
  );

  const theads = [
    'participationStatus',
    'spacesAdded',
    'name',
    'company',
    'role',
    'area',
    'email',
    'phone',
  ];

  const tableBodyMaxHeight = window.innerHeight * 0.5;

  const handleSubmit = useCallback(async event => {
    event.preventDefault()
    setSubmitting(true)
    await editProjectParticipants(formValues)
    setSubmitting(false)
  }, [
    editProjectParticipants,
    formValues
  ])

  return (
    <form onSubmit={handleSubmit}>
      <div
        style={{
          maxHeight: `${tableBodyMaxHeight}px`,
          overflowY: 'auto',
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                style={{
                  padding: '15px',
                  backgroundColor: 'rgb(227, 238, 255)',
                  borderBottom: '2px solid black',
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleSelectAll}
                >
                  {!allSelected
                    ? t('participants.selectAll')
                    : t('participants.clearSelection')}
                </Button>
              </TableCell>
              {theads &&
                theads.map(item => (
                  <TableCell
                    key={item}
                    sx={inlineStyles.tableLabel}
                    onClick={() => changeSortOrder(item)}
                  >
                    <TableSortLabel
                      active={orderByStr === item}
                      direction={sortOrder}
                      sx={inlineStyles.sortLabel}
                    >
                      {t(`participants.${item}`)}
                    </TableSortLabel>
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(user => {
              const uid = R.path(['profile', 'uid'], user);

              const isOwnedByThisUser = val =>
                R.path(['users', uid], val) !== undefined;
              const filteredRelatedToThisUserAttachedSpaces = R.pickBy(
                isOwnedByThisUser,
                filteredAttachedSpaces
              );

              const filteredRelatedToThisUserActiveAndCandidateAttachedSpaces = R.pickBy(
                isActiveAndCandidate,
                filteredRelatedToThisUserAttachedSpaces
              );

              return (
                <Field
                  component={returnField('CheckBoxListUsers')}
                  filteredAttachedActiveAndCandidateSpaces={
                    filteredRelatedToThisUserActiveAndCandidateAttachedSpaces
                  }
                  filteredAttachedSpaces={
                    filteredRelatedToThisUserAttachedSpaces
                  }
                  id={uid}
                  item={user}
                  key={uid}
                  name={uid}
                  pid={doc}
                  projectUsers={projectUsers}
                  setValue={setFormValues}
                  t={t}
                  type="text"
                  uid={uid}
                  value={formValues[uid] || false}
                />
              );
            })}
          </TableBody>
        </Table>
      </div>
      {isDraft ? (
        <div>
          {t('participants.promptStatusChange')}
          <br />
        </div>
      ) : null}
      <br />
      <Button
        style={{ marginRight: '10px' }}
        variant="contained"
        color="secondary"
        disabled={isDraft || pristine || submitting}
        onClick={resetForm}
      >
        {t('common.cancel')}
        <Icon sx={inlineStyles.rightIcon}>cloud_upload</Icon>
      </Button>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={isDraft || pristine || submitting}
      >
        {t('participants.saveParticipants')}
        <Icon sx={inlineStyles.rightIcon}>cloud_upload</Icon>
      </Button>
    </form>
  );
};

const mapStateToProps = (state, ownProps) => {
  const attachedSpaces = R.path(['space', 'attachedSpaces'], state);
  const pid = R.path(['doc'], ownProps);
  const isPartOfProject = (val) => val.project === pid;
  const filteredAttachedSpaces = R.pickBy(isPartOfProject, attachedSpaces);

  const users = R.path(['user', 'users'], state);
  const projectUsers = R.path(['project', 'projects', pid, 'users'], state);
  const projectStatus = R.path(
    ['project', 'projects', pid, 'projectInfo', 'status'],
    state
  );

  const markSelectedUsers = (num, key) =>
    !!projectUsers.hasOwnProperty(key);
  const initialValues = R.mapObjIndexed(markSelectedUsers, users);

  return {
    initialValues,
    projectStatus,
    projectUsers,
    filteredAttachedSpaces,
    usersState: users,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  editProjectParticipants: values =>
    dispatch(
      editProjectParticipants(
        values,
        R.path(['doc'], ownProps),
        R.path(['project', 'projectInfo', 'status'], ownProps),
        R.path(['t'], ownProps)
      )
    ),
});

export default withTranslation('translations')(
  connect(mapStateToProps, mapDispatchToProps)(AddParticipants)
);
